import { useEffect, useMemo } from 'react';
import { plannedRecipesToWarm, prefetchRecipe } from '../services/recipePrefetch';
import { useToday } from './useToday';

/**
 * How long after the planner settles before it reads ahead. Boot asks
 * Firestore for the plan, the index and the pantry, and on Safari's long
 * polling a read here would queue in front of them.
 */
const SETTLE_MS = 3000;

/**
 * Reads today's and tomorrow's recipes once the planner is quiet, so opening
 * tonight's dinner draws from memory (#874). Keyed on the ids, not the meals:
 * every Firestore snapshot recreates the meal objects.
 */
export function usePrefetchPlannedRecipes(
  meals: readonly { recipeId: string; scheduledFor: string; mealType: string; mode?: 'proposed' | 'accepted' }[],
): void {
  const today = useToday();
  const key = useMemo(() => plannedRecipesToWarm(meals, today).join(','), [meals, today]);

  useEffect(() => {
    if (!key) return undefined;
    let idle: number | null = null;
    const timer = setTimeout(() => {
      const run = () => key.split(',').forEach((id) => prefetchRecipe(id));
      if (typeof requestIdleCallback === 'function') idle = requestIdleCallback(run, { timeout: 2000 });
      else run();
    }, SETTLE_MS);
    return () => {
      clearTimeout(timer);
      if (idle !== null) cancelIdleCallback(idle);
    };
  }, [key]);
}
